'use client';

import {
  type OneTrustEnvironment,
  OneTrustProvider,
} from '@jouzen/onetrust-consent-manager';
import { env } from 'next-runtime-env';
import type { ReactNode } from 'react';

import { hipaaPlugin } from '@/analytics/plugins/hipaaPlugin';
import { debugAnalytics } from '@/utils/debugAnalytics';

const SEGMENT_PLUGINS = [hipaaPlugin];

interface OneTrustEnvProps {
  environment?: OneTrustEnvironment;
  onetrustKey?: string;
  segmentKey?: string;
}

function getMissingProps(props: OneTrustEnvProps): string[] {
  const missing: string[] = [];
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined) {
      missing.push(key);
    }
  }
  return missing;
}

const OneTrustConsentProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const envProps: OneTrustEnvProps = {
    environment: env('NEXT_PUBLIC_OURA_ENV') as OneTrustEnvironment,
    segmentKey: env('NEXT_PUBLIC_SEGMENT_KEY'),
    onetrustKey: env('NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT'),
  };

  const missingProps = getMissingProps(envProps);
  if (missingProps.length > 0) {
    debugAnalytics('Missing required environment variables', {
      data: {
        missingProperties: missingProps.join(', '),
        component: 'OneTrustConsentProvider',
      },
      level: 'error',
      logToDatadog: 'OneTrustConsentProvider',
    });
    return <>{children}</>;
  }

  return (
    <OneTrustProvider
      applicationName="ecom-order-portal"
      environment={envProps.environment!}
      onetrustKey={envProps.onetrustKey!}
      segmentKey={envProps.segmentKey!}
      enableEventQueue={true}
      segmentPlugins={SEGMENT_PLUGINS}
      onScriptLoad={() =>
        debugAnalytics('OneTrust ready', {
          data: { component: 'OneTrustConsentProvider' },
        })
      }
      onScriptError={(error: Error) =>
        debugAnalytics('OneTrust error', {
          data: { error, component: 'OneTrustConsentProvider' },
          level: 'error',
          exception: true,
          logToDatadog: 'OneTrustConsentProvider',
        })
      }
    >
      {children}
    </OneTrustProvider>
  );
};

export default OneTrustConsentProvider;
